import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";
import { connectSocket, getSocket, initSocketNotifications } from "../socket";
import axios from "axios";

const setAuthHeader = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

// Online presence: listen to both event names and normalize ids to strings.
const bindPresence = (socket, set) => {
  if (!socket) return;

  const onList = (users) => {
    console.log("🟢 presence event received:", users); // DEBUG (baad mein hata denge)
    const list = Array.isArray(users) ? users : [];
    set({ onlineUsers: list.map((u) => String(u?._id || u)) });
  };
  const onDisconnect = () => set({ onlineUsers: [] });

  socket.off("onlineUsers");
  socket.off("getOnlineUsers");
  socket.off("disconnect", onDisconnect);

  socket.on("onlineUsers", onList);
  socket.on("getOnlineUsers", onList);
  socket.on("disconnect", onDisconnect);
};

const unbindPresence = (socket) => {
  if (!socket) return;
  try {
    socket.off("onlineUsers");
    socket.off("getOnlineUsers");
  } catch {}
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      authUser: null,
      socket: null,
      isLoggingIn: false,
      isCheckingAuth: true,
      onlineUsers: [],

      setAuthUser: (user) => set({ authUser: user }),
      setSocket: (socket) => set({ socket }),
      setOnlineUsers: (users) =>
        set({
          onlineUsers: (users || []).map((u) => String(u?._id || u)),
        }),

      // ✅ VERIFY (called on app load)
      checkAuth: async () => {
        set({ isCheckingAuth: true });

        try {
          const token = localStorage.getItem("token");
          if (!token) {
            setAuthHeader(null);
            set({ authUser: null, isCheckingAuth: false });
            return;
          }

          setAuthHeader(token);

          const res = await axiosInstance.get("/auth/verify");
          const user = res?.data?.user || res?.data;

          if (res?.data?.token) {
            localStorage.setItem("token", res.data.token);
            setAuthHeader(res.data.token);
          }

          // connect socket once per session/user
          let socket = get().socket;
          if (!socket) {
            socket = connectSocket(user._id);
          }

          // attach presence listeners (removes old ones first)
          bindPresence(socket, set);

          set({
            authUser: { ...user, token: localStorage.getItem("token") || token },
            socket,
            isCheckingAuth: false,
          });

          if ("Notification" in window && Notification.permission === "granted") {
            initSocketNotifications();
          }
        } catch (error) {
          const msg =
            error?.response?.data?.message ||
            error?.message ||
            "Verify failed";
          console.error("❌ Verify error:", error?.response?.data || msg);

          localStorage.removeItem("token");
          setAuthHeader(null);

          const sock = get().socket;
          if (sock) {
            try {
              unbindPresence(sock);
              sock.disconnect();
            } catch {}
          }

          set({ authUser: null, socket: null, onlineUsers: [], isCheckingAuth: false });
        }
      },

      // ✅ LOGIN
      login: async (credentials) => {
        set({ isLoggingIn: true });
        try {
          const res = await axiosInstance.post("/auth/login", credentials);
          const user = res?.data?.user;
          const token = res?.data?.token;

          if (!user || !token) {
            throw new Error("Invalid login response (missing user/token)");
          }

          localStorage.setItem("token", token);
          setAuthHeader(token);

          // (re)connect socket
          const prev = get().socket;
          if (prev) {
            try {
              unbindPresence(prev);
              prev.disconnect();
            } catch {}
          }
          const socket = connectSocket(user._id);
          bindPresence(socket, set);

          set({ authUser: { ...user, token }, socket });
          toast.success("Logged in successfully");
          return user;
        } catch (error) {
          const msg =
            error?.response?.data?.message ||
            error?.message ||
            "Login failed";
          toast.error(msg);
          throw error;
        } finally {
          set({ isLoggingIn: false });
        }
      },

      // ✅ PROFILE UPDATE
      updateProfile: async (data) => {
        try {
          const res = await axios.put("/api/profile/update", data, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          });

          const updatedUser = res?.data?.user || {};
          set((state) => ({
            authUser: { ...state.authUser, ...updatedUser },
          }));
          toast.success("Profile updated");
        } catch (err) {
          console.error("Update profile failed:", err?.response?.data || err?.message);
          toast.error("Failed to update profile");
        }
      },

      // ✅ LOGOUT
      logout: async () => {
        try {
          await axiosInstance.post("/auth/logout").catch(() => {});
        } finally {
          localStorage.removeItem("token");
          setAuthHeader(null);

          const socket = getSocket();
          if (socket) {
            try {
              unbindPresence(socket);
              socket.disconnect();
            } catch {}
          }

          set({ authUser: null, socket: null, onlineUsers: [] });
          toast.success("Logged out successfully");
        }
      },

      // ✅ Call this ONLY from a user click (button) to avoid Chrome violation
      enableNotifications: async () => {
        if (!("Notification" in window)) return false;
        try {
          const perm = await Notification.requestPermission();
          if (perm === "granted") {
            initSocketNotifications();
            toast.success("Notifications enabled");
            return true;
          } else {
            toast("Notifications permission denied");
            return false;
          }
        } catch {
          return false;
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({ authUser: state.authUser }),
    }
  )
);